import React, { useState, useEffect } from 'react';
import {
  Building2, ChevronRight, ExternalLink, Award, Handshake, Users, Layers,
  Factory, Car, Cpu, MonitorPlay, Zap,
  UserPlus, Wrench, ShieldCheck, TrendingUp, Presentation,
  CheckCircle, ArrowRight
} from 'lucide-react';
import Layout from '../component/layout/Layout';
import ConsultationModal from '../component/ConsultationModal';

// Import your client logos

import tm from "../assets/images/tata_motorspng.png";
import ek from "../assets/images/Escorts_Kubota_Limited.png";
import cm from "../assets/images/caparo_maruti.png";
import sh from "../assets/images/shayadari_hospital.png";
import mh from "../assets/images/Max-Healthcare.png";
import gh from "../assets/images/jaipur_golden.png";
import mc from "../assets/images/minda_corp.png";
import da from "../assets/images/dabar_automotive.png";
import amass from "../assets/images/Amass_New_Logo 2025.png";
import jktyre from "../assets/images/jk-tyre.webp"
import continental from "../assets/images/Continental_Hospital_Logo.jpg"
import fortis from "../assets/images/fortis.png"
import jtekt from "../assets/images/④JTEKT_logo_Positive_type,_Black_+_Red.jpg"
import tatamtrs from "../assets/images/Tata-Motors.png"

import heroBg from "../assets/images/automobile.webp";
import section2Img from "../assets/images/carousel_1.jpg";

export default function IndustrySpecificPage() {
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' ? window.innerWidth >= 1024 : true);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 640 : false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredIndCard, setHoveredIndCard] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
      setIsMobile(window.innerWidth < 640);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const clients = [
    { name: 'Tata Motors', logo: tatamtrs, category: 'Automotive' },
    { name: 'Escorts Kubota', logo: ek, category: 'Manufacturing' },
    { name: 'Caparo Maruti', logo: cm, category: 'Automotive' },
    { name: 'Sahyadri Hospital', logo: sh, category: 'Healthcare' },
    { name: 'Max Healthcare', logo: mh, category: 'Healthcare' },
    { name: 'Jaipur Golden Hospital', logo: gh, category: 'Healthcare' },
    { name: 'Continental Hospital', logo: continental, category: 'Healthcare' },
    { name: 'JK Tyre', logo: jktyre, category: 'Automotive' },
    { name: 'JTEKT', logo: jtekt, category: 'Training' },
    { name: 'Fortis Hospital', logo: fortis, category: 'Healthcare' },
  ];

  const industries = [
    { name: 'Manufacturing & Engineering', icon: Factory },
    { name: 'Electric Vehicle Technology', icon: Car },
    { name: 'Industrial Automation', icon: Cpu },
    { name: 'Design & Engineering Software', icon: MonitorPlay },
    { name: 'Emerging Industry Technologies', icon: Zap }
  ];

  const solutions = [
    { text: 'Employee onboarding and orientation training', icon: UserPlus },
    { text: 'Technical skill development programs', icon: Wrench },
    { text: 'Certification and compliance training', icon: ShieldCheck },
    { text: 'Continuous workforce upskilling', icon: TrendingUp },
    { text: 'Partner and distributor training programs', icon: Presentation }
  ];

  const handleClientClick = (clientName) => {
    window.location.href = "https://admin.skillserve.in/";
  };

  const pageWrapStyle = {
    width: '100%',
    minHeight: '100vh',
    background: '#ffffff',
    fontFamily: "'Inter', 'Roboto', sans-serif"
  };

  // --- 1. HERO SECTION ---
  const heroStyle = {
    width: '100%',
    position: 'relative',
    minHeight: isDesktop ? '60vh' : '50vh', // Reduced height
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundImage: `url(${heroBg})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
    padding: isMobile ? '80px 20px 100px' : '100px 20px 120px',
    overflow: 'hidden'
  };

  const heroOverlayStyle = {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(135deg, rgba(11, 49, 97, 0.75) 0%, rgba(6, 30, 61, 0.90) 100%)', // slightly less opaque
    backdropFilter: 'blur(5px)',
    WebkitBackdropFilter: 'blur(5px)',
    zIndex: 1
  };

  const heroContentStyle = {
    position: 'relative',
    zIndex: 2,
    maxWidth: '900px',
    textAlign: 'center',
    color: '#ffffff'
  };

  const WaveDivider = () => (
    <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', overflow: 'hidden', lineHeight: 0, zIndex: 3 }}>
      <svg viewBox="0 0 1440 320" preserveAspectRatio="none" style={{ position: 'relative', display: 'block', width: 'calc(100% + 1.3px)', height: isMobile ? '60px' : '100px' }}>
        <path fill="#ffffff" fillOpacity="1" d="M0,192L48,197.3C96,203,192,213,288,197.3C384,181,480,139,576,138.7C672,139,768,181,864,208C960,235,1056,245,1152,234.7C1248,224,1344,192,1392,176L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
      </svg>
    </div>
  );

  // --- COMMON STYLES ---
  const sectionContentStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: isDesktop ? '80px 24px' : '60px 20px',
  };

  const titleStyle = {
    fontFamily: "'Roboto Slab', serif",
    fontSize: isDesktop ? '2.5rem' : '2rem',
    fontWeight: 800,
    color: '#0b3161',
    marginBottom: '24px',
    lineHeight: 1.2
  };

  const descStyle = {
    fontSize: '1.1rem',
    lineHeight: 1.8,
    color: '#475569',
    marginBottom: '20px'
  };

  return (
    <Layout>
      <div style={pageWrapStyle}>

        {/* 1. HERO SECTION */}
        <section style={heroStyle}>
          <div style={heroOverlayStyle} />
          <div style={heroContentStyle}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: 'rgba(253, 214, 73, 0.15)', color: '#fdd649',
              padding: '8px 20px', borderRadius: '999px', fontWeight: 600,
              textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '24px',
              border: '1px solid rgba(253, 214, 73, 0.3)'
            }}>
              <Building2 size={16} />
              Enterprise Solutions
            </div>
            <h1 style={{ ...titleStyle, color: '#ffffff', fontSize: isDesktop ? '3.5rem' : '2.5rem', textShadow: '0 4px 20px rgba(0,0,0,0.4)', marginBottom: '32px' }}>
              Industry-Specific Training Programs
            </h1>
            <p style={{ ...descStyle, color: '#e2e8f0', fontSize: '1.25rem', maxWidth: '800px', margin: '0 auto' }}>
              SkillServe offers specialized training programs designed to address real workforce skill requirements across different sectors through impactful digital learning.
            </p>
          </div>
          <WaveDivider />
        </section>

        {/* 2. INTRODUCTION SECTION */}
        <section style={{ ...sectionContentStyle, padding: isDesktop ? '100px 24px' : '60px 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? '1fr 1fr' : '1fr', gap: isDesktop ? '60px' : '40px', alignItems: 'center' }}>
            {/* Left Image Area */}
            <div style={{ position: 'relative', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 20px 40px rgba(11,49,97,0.15)' }}>
              <img
                src={section2Img}
                alt="Industry Training"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
              <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'linear-gradient(45deg, rgba(11,49,97,0.4) 0%, transparent 100%)', pointerEvents: 'none' }} />
            </div>

            {/* Right Text Area */}
            <div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                background: 'rgba(253, 214, 73, 0.1)', color: '#0b3161',
                padding: '6px 16px', borderRadius: '999px', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '20px',
                border: '1px solid rgba(253, 214, 73, 0.4)'
              }}>
                <Award size={14} /> Introduction
              </div>
              <h2 style={{ ...titleStyle, fontSize: isDesktop ? '2.5rem' : '1.75rem', lineHeight: 1.3 }}>
                Practical Skills for Modern Workspaces.
              </h2>
              <p style={{ ...descStyle, fontSize: '1.2rem', color: '#334155', fontWeight: 500 }}>
                SkillServe offers industry-specific training programs designed to address real workforce skill requirements across different sectors.
              </p>
              <p style={{ ...descStyle, fontSize: '1.05rem' }}>
                Through our digital Learning Management System (LMS) and expert-led courses, organizations and professionals can access structured learning programs aligned with modern industry practices.
              </p>
              <p style={{ ...descStyle, fontSize: '1.05rem', marginBottom: '32px' }}>
                Our programs focus on practical skills, emerging technologies, and workplace readiness to help learners build capabilities that are relevant to today’s industrial environments.
              </p>
              <div style={{ background: 'rgba(253, 214, 73, 0.15)', padding: '24px', borderRadius: '16px', borderLeft: '4px solid #fdd649' }}>
                <p style={{ ...descStyle, fontSize: '1.05rem', fontWeight: 600, color: '#0b3161', margin: 0, fontStyle: 'italic' }}>
                  All training programs are delivered through the SkillServe LMS platform, enabling organizations to manage learning, track progress, and scale workforce training efficiently.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 3. INDUSTRIES COVERED */}
        <section style={{ background: '#f8fafc', padding: isDesktop ? '40px 0 100px' : '40px 0 80px' }}>
          <div style={sectionContentStyle}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <div style={{ display: 'inline-flex', justifyContent: 'center', marginBottom: '16px', background: '#e2e8f0', padding: '12px', borderRadius: '16px' }}>
                <Layers size={32} color="#0b3161" />
              </div>
              <h2 style={{ ...titleStyle, fontSize: isDesktop ? '2.5rem' : '2rem' }}>Industries Covered</h2>
              <p style={{ ...descStyle, maxWidth: '700px', margin: '0 auto', fontSize: '1.15rem' }}>
                SkillServe develops training programs aligned with the needs of multiple industries, designed to ensure that learners gain practical knowledge and industry-relevant capabilities that support career growth.
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isDesktop ? 'repeat(auto-fit, minmax(280px, 1fr))' : '1fr',
              gap: '24px',
              justifyContent: 'center'
            }}>
              {industries.map((ind, i) => {
                const isHovered = hoveredIndCard === i;
                const Icon = ind.icon;
                return (
                  <div
                    key={i}
                    onMouseEnter={() => setHoveredIndCard(i)}
                    onMouseLeave={() => setHoveredIndCard(null)}
                    style={{
                      background: '#ffffff',
                      borderRadius: '20px',
                      padding: '40px 24px',
                      boxShadow: isHovered ? '0 20px 40px rgba(11,49,97,0.1)' : '0 4px 12px rgba(0,0,0,0.05)',
                      transform: isHovered ? 'translateY(-5px)' : 'none',
                      transition: 'all 0.3s ease',
                      border: '1px solid #e2e8f0',
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '24px'
                    }}
                  >
                    <div style={{
                      width: '72px', height: '72px', borderRadius: '50%',
                      background: isHovered ? 'linear-gradient(135deg, #0b3161 0%, #1a4a82 100%)' : 'rgba(253, 214, 73, 0.15)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: isHovered ? '#fdd649' : '#0b3161',
                      transition: 'all 0.3s ease'
                    }}>
                      <Icon size={36} />
                    </div>
                    <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#081426', margin: 0, lineHeight: 1.4 }}>
                      {ind.name}
                    </h3>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* 4. TRAINING SOLUTIONS FOR ORGANIZATIONS */}
        <section style={sectionContentStyle}>
          <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? '1fr 1fr' : '1fr', gap: '60px', alignItems: 'center' }}>
            <div>
              <h2 style={{ ...titleStyle, fontSize: isDesktop ? '2.5rem' : '2rem' }}>
                Training Solutions for Organizations
              </h2>
              <p style={descStyle}>
                SkillServe also supports organizations in delivering structured workforce training programs through our LMS platform.
              </p>
              <p style={descStyle}>
                Companies can deploy industry-specific learning modules, monitor employee progress, and scale training initiatives across teams and locations through a centralized digital learning environment.
              </p>
              <div style={{ paddingLeft: '20px', borderLeft: '4px solid #fdd649', marginTop: '32px' }}>
                <p style={{ ...descStyle, margin: 0, fontWeight: 600, color: '#0b3161', fontSize: '1.15rem' }}>
                  Our training solutions support organizations in building a skilled and future-ready workforce.
                </p>
              </div>
            </div>

            <div style={{ background: 'linear-gradient(135deg, #0b3161 0%, #1a4a82 100%)', borderRadius: '24px', padding: isDesktop ? '48px' : '32px', color: '#ffffff', boxShadow: '0 20px 40px rgba(11,49,97,0.2)' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '32px', color: '#fdd649' }}>
                Organizations can use SkillServe for:
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {solutions.map((sol, i) => {
                  const Icon = sol.icon;
                  return (
                    <div key={i} style={{ display: 'flex', gap: '16px', alignItems: 'center', background: 'rgba(255,255,255,0.05)', padding: '16px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)' }}>
                      <div style={{ background: 'rgba(253, 214, 73, 0.1)', padding: '12px', borderRadius: '12px' }}>
                        <Icon size={24} color="#fdd649" />
                      </div>
                      <span style={{ fontSize: '1.1rem', lineHeight: 1.5, fontWeight: 500 }}>{sol.text}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        {/* 5. EXPERT LED LEARNING */}
        <section style={{ ...sectionContentStyle, paddingBottom: isDesktop ? '100px' : '60px' }}>
          <div style={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            borderRadius: '24px',
            padding: isDesktop ? '60px' : '40px',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            border: '1px solid #cbd5e1'
          }}>
            <Award size={160} color="#0b3161" style={{ position: 'absolute', opacity: 0.05, right: '-30px', bottom: '-40px' }} />
            <Award size={120} color="#0b3161" style={{ position: 'absolute', opacity: 0.03, left: '-20px', top: '-20px' }} />

            <h2 style={titleStyle}>Expert-Led Learning</h2>
            <p style={{ ...descStyle, maxWidth: '800px', margin: '0 auto 20px', fontSize: '1.15rem' }}>
              SkillServe collaborates with Subject Matter Experts and industry professionals to design learning programs that reflect real workplace requirements.
            </p>
            <p style={{ ...descStyle, maxWidth: '800px', margin: '0 auto', fontSize: '1.15rem' }}>
              Courses are developed with a focus on practical applications, industry practices, and emerging technologies so that learning remains relevant and impactful.
            </p>
          </div>
        </section>

        {/* 6. FINAL CALL TO ACTION */}
        <section style={{
          background: 'linear-gradient(135deg, #0b3161 0%, #1a4a82 100%)',
          padding: isDesktop ? '100px 24px' : '80px 20px',
          color: '#ffffff',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Creative dot pattern background */}
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 2px, transparent 0)',
            backgroundSize: '32px 32px',
            opacity: 0.6,
            zIndex: 0
          }} />
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at center, transparent 0%, #0b3161 80%)',
            zIndex: 1
          }} />

          <div style={{ maxWidth: '800px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
            <h2 style={{ fontFamily: "'Roboto Slab', serif", fontSize: isDesktop ? '3.25rem' : '2.25rem', fontWeight: 800, marginBottom: '24px', lineHeight: 1.2 }}>
              Build Industry-Ready Skills With SkillServe
            </h2>
            <p style={{ fontSize: '1.25rem', color: '#cbd5e1', marginBottom: '48px', lineHeight: 1.6, fontWeight: 300 }}>
              Explore industry-focused training programs designed to help individuals grow and organizations develop future-ready teams.
            </p>
            <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => window.location.href = "https://admin.skillserve.in/"}
                style={{
                  background: 'linear-gradient(90deg, #fdd649, #f7c42d)', color: '#081426',
                  border: 'none', padding: '16px 36px', borderRadius: '12px', fontSize: '1.1rem',
                  fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                  boxShadow: '0 8px 24px rgba(253, 214, 73, 0.4)', transition: 'transform 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-3px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'none'}
              >
                Explore Courses <ArrowRight size={20} />
              </button>
              <button
                onClick={() => setIsModalOpen(true)}
                style={{
                  background: 'rgba(255,255,255,0.05)', color: '#ffffff',
                  border: '2px solid rgba(255,255,255,0.4)', padding: '14px 36px',
                  borderRadius: '12px', fontSize: '1.1rem', fontWeight: 600,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                  transition: 'background 0.2s ease, transform 0.2s ease',
                  backdropFilter: 'blur(4px)'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.transform = 'none'; }}
              >
                Partner With SkillServe <Handshake size={20} />
              </button>
            </div>
          </div>
        </section>

        {/* 7. CLIENTS GRID REGION */}
        <section style={{ width: '100%', padding: isDesktop ? '100px 24px' : '80px 20px', background: '#f8fafc' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '56px' }}>
              <h2 style={titleStyle}>Our Customers</h2>
              <p style={{ ...descStyle, maxWidth: '700px', margin: '0 auto' }}>Leading organizations across sectors trust SkillServe for their workforce development needs.</p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isDesktop ? 'repeat(3, 1fr)' : isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: isDesktop ? '36px' : isMobile ? '28px' : '32px',
            }}>
              {clients.map((client, index) => {
                const isHovered = hoveredCard === index;

                return (
                  <div
                    key={index}
                    onMouseEnter={() => setHoveredCard(index)}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => handleClientClick(client.name)}
                    role="button"
                    tabIndex={0}
                    style={{
                      background: '#ffffff',
                      borderRadius: '20px',
                      padding: isMobile ? '32px 24px' : '44px 32px',
                      minHeight: isMobile ? '220px' : '280px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #e6eef8',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      transform: isHovered ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
                      boxShadow: isHovered ? '0 24px 48px rgba(11, 49, 97, 0.14)' : '0 6px 20px rgba(11, 49, 97, 0.05)',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <div style={{
                      position: 'absolute', top: '20px', right: '20px', width: '36px', height: '36px',
                      borderRadius: '50%', background: isHovered ? 'linear-gradient(135deg, #fdd649 0%, #f7c42d 100%)' : 'rgba(11, 49, 97, 0.08)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      opacity: isHovered ? 1 : 0.8, transition: 'all 0.3s ease',
                      color: isHovered ? '#081426' : '#0b3161', transform: isHovered ? 'rotate(45deg)' : 'rotate(0)'
                    }}>
                      <ExternalLink size={16} />
                    </div>

                    <div style={{ width: '100%', height: isMobile ? '120px' : '160px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px', position: 'relative' }}>
                      <img
                        src={client.logo}
                        alt={`${client.name} logo`}
                        style={{ maxWidth: '90%', maxHeight: '100%', objectFit: 'contain', opacity: isHovered ? 1 : 0.92, transition: 'all 0.3s ease', transform: isHovered ? 'scale(1.08)' : 'scale(1)' }}
                        onError={(e) => { e.target.src = `https://via.placeholder.com/200x100/0b3161/ffffff?text=${encodeURIComponent(client.name)}`; }}
                      />
                    </div>

                    <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0b3161', textAlign: 'center', marginBottom: '10px' }}>
                      {client.name}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

      </div>
      <ConsultationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        defaultQueryOption="Looking industry specific training solutions"
      />
    </Layout>
  );
}